import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { ChevronLeft, IconButton } from "@coolremote/shared";
import { useStoreActions } from "../../../models/RootStore";
import { SiteDetailsForm } from "../NewUserFlow/SiteDetailsStep";
import { SiteDetailsValues } from "../NewUserFlow/newUserFlowState";
import { associateDevice } from "./existingUserFlowApi";
import { ExistingUserFlowState, hasCustomerSelected } from "./existingUserFlowState";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./ExistingUserFlow.module.css";

/**
 * "+ Create new site" sub-step off Select Site (CM-619). Reuses
 * `SiteDetailsForm` from the new-user branch as-is, per its own doc comment.
 * A route of its own (not a dialog), same as `NewCustomerStep`; on save it
 * goes straight on to discovery with the new site already selected, rather
 * than back through Select Site.
 */
const NewSiteStep: React.FC = () => {
  const history = useHistory<ExistingUserFlowState | undefined>();
  const deviceState = history.location.state;
  const createSite = useStoreActions((actions) => actions.createSite);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!hasCustomerSelected(deviceState)) {
      history.replace("/register-device/existing-user/select-customer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasCustomerSelected(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  const handleSubmit = async (siteDetails: SiteDetailsValues) => {
    setErrorMessage(undefined);
    setIsSubmitting(true);
    try {
      // Same call the authenticated Add Site screen uses (`Site.create` via
      // the `createSite` thunk) — this branch already has a session from
      // LoginStep, so it's just the normal site-creation path.
      const site: { id: string } = await createSite({ customer: deviceState.customerId, ...siteDetails });
      const { deviceId } = await associateDevice(site.id, deviceState.serial, deviceState.pin, deviceState.description);
      const nextState = {
        ...deviceState,
        siteId: site.id,
        siteName: siteDetails.name,
        siteDetails,
        deviceId
      } as ExistingUserFlowState;
      // Attach the created site to *this* history entry too, so that going
      // back from Discovery lands here with the choice restored.
      history.replace(history.location.pathname, nextState);
      history.push("/register-device/existing-user/discovery", nextState);
      // No setIsSubmitting(false) here — the push above unmounts this component.
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : t`Something went wrong. Please try again`);
      setIsSubmitting(false);
    }
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`New Site`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        {errorMessage && <div className={styles.errorBanner}>{errorMessage}</div>}
        <SiteDetailsForm
          initialValues={{ name: deviceState.customerName }}
          disabled={isSubmitting}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default NewSiteStep;
